export type CounterPosterPreset = "brand" | "wood" | "royal" | "garden";
export type CounterPosterLanguage = "en" | "hi";

export interface CounterPosterDesign {
  orgName: string;
  appName: string;
  orgLogoUrl: string | null;
  counterName: string;
  counterPhone: string | null;
  qrDataUrl: string;
  language: CounterPosterLanguage;
  preset: CounterPosterPreset;
  primary: string;
  accent: string;
  backgroundDataUrl: string | null;
  backgroundOverlay: number;
}

export const COUNTER_POSTER_PRESETS: Array<{
  id: CounterPosterPreset;
  name: string;
  description: string;
  primary: string;
  accent: string;
  paper: string;
  text: string;
  backgroundImage: string;
}> = [
  {
    id: "brand",
    name: "Brand classic",
    description: "Your organization colors",
    primary: "#C54E1B",
    accent: "#D7A749",
    paper: "#FFF9F3",
    text: "#2C211B",
    backgroundImage: "radial-gradient(ellipse at 80% 12%, #f1d7bd 0%, transparent 34%), linear-gradient(145deg, #fffaf5 0%, #f5e7d8 100%)",
  },
  {
    id: "wood",
    name: "Wood & gold",
    description: "Warm premium finish",
    primary: "#7B3A1E",
    accent: "#E5BD5B",
    paper: "#512915",
    text: "#FFF9EF",
    backgroundImage: "repeating-linear-gradient(91deg, #512915 0%, #63331a 8%, #75401f 15%, #512915 23%), linear-gradient(145deg, #31190f, #75401f)",
  },
  {
    id: "royal",
    name: "Royal blue",
    description: "Navy with a gold accent",
    primary: "#153E72",
    accent: "#E8BD59",
    paper: "#12345D",
    text: "#F8FAFF",
    backgroundImage: "radial-gradient(ellipse at 15% 10%, #315d8c 0%, transparent 38%), linear-gradient(145deg, #0b1f3b 0%, #174776 100%)",
  },
  {
    id: "garden",
    name: "Fresh green",
    description: "Deep green with a soft ivory accent",
    primary: "#236448",
    accent: "#E9D69B",
    paper: "#174A36",
    text: "#F7FFF9",
    backgroundImage: "radial-gradient(ellipse at 85% 12%, #3e8061 0%, transparent 35%), linear-gradient(145deg, #103a2b 0%, #236448 100%)",
  },
];

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character]!);
}

function validColor(value: string, fallback: string): string {
  return /^#[\da-f]{6}$/i.test(value) ? value : fallback;
}

export function createCounterPosterHtml(design: CounterPosterDesign, autoPrint = false): string {
  const preset = COUNTER_POSTER_PRESETS.find((item) => item.id === design.preset) ?? COUNTER_POSTER_PRESETS[0]!;
  const primary = validColor(design.primary, preset.primary);
  const accent = validColor(design.accent, preset.accent);
  const overlay = Math.max(0, Math.min(80, design.backgroundOverlay)) / 100;
  const textColor = design.backgroundDataUrl ? "#FFFFFF" : preset.text;
  const backgroundImage = design.backgroundDataUrl
    ? `linear-gradient(rgba(17, 11, 8, ${overlay}), rgba(17, 11, 8, ${overlay})), url("${design.backgroundDataUrl}")`
    : preset.backgroundImage;
  const orgName = escapeHtml(design.orgName);
  const counterName = escapeHtml(design.counterName);
  const phone = design.counterPhone ? escapeHtml(design.counterPhone) : "";
  const appName = escapeHtml(design.appName);
  const logo = design.orgLogoUrl
    ? `<img class="logo" src="${escapeHtml(design.orgLogoUrl)}" alt="${orgName} logo">`
    : `<span class="logo-fallback">${escapeHtml((design.orgName.trim()[0] ?? "G").toLocaleUpperCase())}</span>`;
  const isHindi = design.language === "hi";
  const copy = isHindi
    ? {
        eyebrow: "आपके लिए खास",
        title: "अपना अकाउंट बनाने के लिए QR कोड स्कैन करें",
        subtitle: "बिना किसी ऐप के",
        counter: "आपका काउंटर",
        footer: "स्कैन करें · रजिस्टर करें · रिवॉर्ड कमाएँ",
        powered: `${appName} द्वारा संचालित`,
        print: "पोस्टर डाउनलोड / प्रिंट करें",
      }
    : {
        eyebrow: "A reward program made for you",
        title: "Scan the QR code to create your account",
        subtitle: "No app needed",
        counter: "Your counter",
        footer: "Scan · Register · Earn rewards",
        powered: `Powered by ${appName}`,
        print: "Download / Print poster",
      };
  const printScript = autoPrint
    ? `<script>window.addEventListener("load",()=>setTimeout(()=>window.print(),500));</script>`
    : "";

  return `<!doctype html>
<html lang="${design.language}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${counterName} — ${isHindi ? "कारीगर रजिस्ट्रेशन" : "Karigar registration"}</title>
<style>
  @page{size:A4;margin:0}
  *{box-sizing:border-box}
  html,body{margin:0;min-height:100%;font-family:"Nirmala UI","Noto Sans Devanagari","Mangal",Arial,sans-serif}
  body{background:#e8e4df;color:${preset.text}}
  .sheet{-webkit-print-color-adjust:exact;print-color-adjust:exact;position:relative;isolation:isolate;display:flex;flex-direction:column;align-items:center;width:210mm;height:297mm;margin:0 auto;padding:14mm 15mm 11mm;overflow:hidden;text-align:center;background-color:${preset.paper};background-image:${backgroundImage};background-size:cover;background-position:center;color:${textColor}}
  .sheet:before,.sheet:after{position:absolute;z-index:-1;content:"";pointer-events:none}
  .sheet:before{inset:5mm;border:1px solid ${accent};opacity:.85}
  .sheet:after{inset:8mm;border:1px solid ${accent};opacity:.3}
  .brand{display:flex;align-items:center;justify-content:center;gap:4mm;min-height:23mm;width:100%;padding:1mm 5mm}
  .brand-rule{width:32mm;height:1.3mm;margin-top:2mm;border-radius:2mm;background:${primary}}
  .logo{max-width:42mm;max-height:18mm;object-fit:contain;border-radius:2mm;background:#fff;padding:2mm}
  .logo-fallback{display:grid;place-items:center;width:17mm;height:17mm;border-radius:50%;background:${primary};color:#fff;font-size:24pt;font-weight:800}
  .org{max-width:130mm;font-size:18pt;line-height:1.15;font-weight:800;letter-spacing:.02em;overflow-wrap:anywhere}
  .eyebrow{margin:11mm 0 2mm;color:${accent};font-size:12pt;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
  h1{max-width:165mm;margin:0;font-size:27pt;line-height:1.17;font-weight:800;text-wrap:balance;text-shadow:0 1px 4px #0002}
  .subtitle{margin:3mm 0 0;color:${accent};font-size:17pt;font-weight:700}
  .divider{display:flex;align-items:center;gap:4mm;width:75%;margin:8mm auto 7mm;color:${accent}}
  .divider:before,.divider:after{height:1px;flex:1;content:"";background:${accent}}
  .star{font-size:19pt;line-height:1}
  .counter{margin:0 0 5mm;font-size:15pt;font-weight:700}
  .counter small{display:block;margin-bottom:1mm;color:${accent};font-size:9pt;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
  .qr-frame{display:grid;place-items:center;width:101mm;height:101mm;border:2mm solid ${accent};background:#fff;padding:4mm;box-shadow:0 4mm 12mm #0003}
  .qr{display:block;width:100%;height:100%;object-fit:contain;background:#fff}
  .bottom{margin-top:auto;width:100%;padding-top:7mm}
  .footer{margin:0;font-size:17pt;font-weight:800}
  .phone{margin:2mm 0 0;font-size:18pt;font-weight:800;letter-spacing:.04em}
  .powered{margin:5mm 0 0;font-size:9pt;opacity:.78}
  .print{position:fixed;z-index:2;top:16px;right:16px;border:0;border-radius:10px;padding:12px 18px;background:${primary};color:#fff;font:700 15px Arial,sans-serif;cursor:pointer;box-shadow:0 3px 12px #0003}
  @media screen{body{display:flex;justify-content:center;align-items:flex-start;padding:12px}.sheet{width:min(100%,440px);height:auto;aspect-ratio:210/297;padding:5.2% 7% 4.5%}.brand{min-height:8%;gap:2%}.logo{max-width:28%;max-height:7%;padding:1%}.logo-fallback{width:9%;height:auto;aspect-ratio:1;font-size:5vw}.org{max-width:70%;font-size:clamp(12px,4vw,19px)}.eyebrow{margin:3% 0 1%;font-size:clamp(7px,2.3vw,11px)}h1{font-size:clamp(15px,5.7vw,26px)}.subtitle{margin-top:1%;font-size:clamp(10px,3.6vw,17px)}.divider{margin:3% auto 2%;gap:2%}.star{font-size:clamp(12px,4vw,18px)}.counter{margin-bottom:2%;font-size:clamp(10px,3.3vw,15px)}.counter small{font-size:clamp(6px,1.8vw,9px)}.qr-frame{width:53%;height:auto;aspect-ratio:1;padding:2.2%;border-width:1.2%}.bottom{padding-top:3%}.footer{font-size:clamp(10px,3.8vw,17px)}.phone{font-size:clamp(11px,4vw,18px)}.powered{margin-top:2%;font-size:clamp(7px,1.8vw,9px)}}
  @media print{body{background:white}.sheet{margin:0;width:210mm;height:297mm}.print{display:none}}
</style></head><body>
${autoPrint ? `<button class="print" onclick="window.print()">${copy.print}</button>` : ""}
<main class="sheet"><header class="brand">${logo}<div class="org">${orgName}</div></header><div class="brand-rule"></div>
<p class="eyebrow">${copy.eyebrow}</p><h1>${copy.title}</h1><p class="subtitle">${copy.subtitle}</p>
<div class="divider"><span class="star">★</span></div>
<p class="counter"><small>${copy.counter}</small>${counterName}</p>
<div class="qr-frame"><img class="qr" src="${design.qrDataUrl}" alt="WhatsApp registration QR code"></div>
<div class="bottom"><p class="footer">${copy.footer}</p>${phone ? `<p class="phone">${phone}</p>` : ""}<p class="powered">${copy.powered}</p></div>
</main>${printScript}</body></html>`;
}
